import { RankingGroupApiItem } from "@/entities/search-ranking/model/types"
import { FilterValue, Region } from "@/shared/types/type/search"

export const HOSPITAL_DIRECTORY_PAGE_SIZE = 30
// 검색 API의 Elasticsearch result window는 10,000이다. 전체 목록의 깊은 페이지를
// 열어 500을 만들지 않고, 전수 발견은 taxonomy 124개 중 결과가 있는 123개
// indexable leaf-region 허브로 분할한다.
export const HOSPITAL_DIRECTORY_MAX_SEARCH_PAGES = 333

export type HospitalDirectoryRoute = {
  page: number
  regionId?: number
  treatmentId?: number
}

export type HospitalDirectoryGroup = {
  regionId: number
  regionName: string
  treatmentId: number
  treatmentName: string
}

export type HospitalDirectoryRegion = {
  regionId: number
  regionName: string
}

// 대표 지역 우선 노출 순서: 수도권(서울·경기·인천) → 광역시 → 나머지 시도 가나다.
// 시도 판별은 regionName 접두("서울 " 등) 기준이라 taxonomy의 지역 ID 변화에 영향받지 않는다.
export const HOSPITAL_DIRECTORY_SIDO_RANKS: readonly string[] = [
  "서울", "경기", "인천", "부산", "대구", "대전", "광주", "울산",
]

// 서울 안에서는 대표 상권(강남/서초)을 최우선으로 노출한다.
const PRIORITY_REGION_NAMES: readonly string[] = ["서울 강남/서초"]

// 리뷰드 대표 분야 순서. 이름이 매칭되지 않는 과목은 뒤에서 가나다.
export const HOSPITAL_DIRECTORY_TREATMENT_RANKS: readonly string[] = ["피부과", "성형외과", "치과"]

// rank·대표 매칭은 표시명 완전일치가 아니라 정규형 기준으로 한다.
// "서울 강남·서초", "피부과 " 같은 표기 변형이 우선순위를 잃지 않게
// NFC 정규화 + trim + 연속 공백 축약 + 구분자(·/ㆍ/／) 통일 후 비교한다.
const normalizeDirectoryName = (name: string) => name
  .normalize("NFC")
  .replace(/[·ㆍ／]/g, "/")
  .replace(/\s+/g, " ")
  .trim()

const rankOf = (ranks: readonly string[], index: number) => (index === -1 ? ranks.length : index)

const sidoRankOf = (regionName: string) => {
  const normalized = normalizeDirectoryName(regionName)
  return rankOf(
    HOSPITAL_DIRECTORY_SIDO_RANKS,
    HOSPITAL_DIRECTORY_SIDO_RANKS.findIndex((sido) => normalized.startsWith(`${sido} `)),
  )
}

const priorityRegionRankOf = (regionName: string) => rankOf(
  PRIORITY_REGION_NAMES,
  PRIORITY_REGION_NAMES.indexOf(normalizeDirectoryName(regionName)),
)

const treatmentRankOf = (treatmentName: string) => rankOf(
  HOSPITAL_DIRECTORY_TREATMENT_RANKS,
  HOSPITAL_DIRECTORY_TREATMENT_RANKS.indexOf(normalizeDirectoryName(treatmentName)),
)

export const compareHospitalDirectoryRegionNames = (left: string, right: string) =>
  sidoRankOf(left) - sidoRankOf(right) ||
  priorityRegionRankOf(left) - priorityRegionRankOf(right) ||
  left.localeCompare(right, "ko")

export const compareHospitalDirectoryTreatmentNames = (left: string, right: string) =>
  treatmentRankOf(left) - treatmentRankOf(right) ||
  left.localeCompare(right, "ko")

export type HospitalDirectoryDistrict = HospitalDirectoryRegion & {
  // 시도 탭 안에서 보여줄 구·시 단위 라벨("서울 강남/서초" → "강남/서초")
  districtName: string
}

// 시도별 드릴다운 1단계. regionName은 `${시도} ${구·시}` 형태이므로 첫 공백 전이 시도다.
// 정렬된 입력에서 같은 시도가 연속하므로 입력 순서를 보존하며 인접 그룹으로 묶는다.
export const groupHospitalDirectoryRegionsBySido = (
  regions: HospitalDirectoryRegion[],
) => {
  const grouped: { sidoName: string; regions: HospitalDirectoryDistrict[] }[] = []
  for (const region of regions) {
    const [sidoName, ...rest] = region.regionName.trim().split(/\s+/)
    const district: HospitalDirectoryDistrict = {
      ...region,
      districtName: rest.length > 0 ? rest.join(" ") : region.regionName,
    }
    const last = grouped[grouped.length - 1]
    if (last && last.sidoName === sidoName) last.regions.push(district)
    else grouped.push({ sidoName, regions: [district] })
  }
  return grouped
}

// 과목별 드릴다운 1단계. 입력(지역 대표순 정렬)에서 과목은 흩어져 있으므로
// 정규형 과목명으로 모으고, 과목 순서는 피부→성형→치과→나머지 가나다,
// 각 과목 안 지역 순서는 입력 순서(대표순)를 보존한다.
export const groupHospitalDirectoryGroupsByTreatment = (
  groups: HospitalDirectoryGroup[],
) => {
  const byTreatment = new Map<string, { treatmentName: string; groups: HospitalDirectoryGroup[] }>()
  for (const group of groups) {
    const key = normalizeDirectoryName(group.treatmentName)
    const bucket = byTreatment.get(key)
    if (bucket) bucket.groups.push(group)
    else byTreatment.set(key, { treatmentName: group.treatmentName, groups: [group] })
  }
  return [...byTreatment.values()].sort((left, right) =>
    compareHospitalDirectoryTreatmentNames(left.treatmentName, right.treatmentName))
}

/**
 * 지역 필터 시트의 왼쪽 사이드바(시도) ↔ 오른쪽 목록(지역 묶음) 분할.
 * regionName은 `${시도} ${구·시}` 형태라 첫 공백 앞이 시도다. 입력에 등장한 순서를
 * 보존해(대표순 정렬) 시도 순서를 만들고, 시도 안 지역 순서도 입력 순서를 유지한다.
 * 합집합을 그대로 분할하므로 링크 총량은 변하지 않는다.
 */
export const groupHospitalDirectoryGroupsBySido = (groups: HospitalDirectoryGroup[]) => {
  const bySido = new Map<string, { sidoName: string; groups: HospitalDirectoryGroup[] }>()
  for (const group of groups) {
    const sidoName = group.regionName.trim().split(/\s+/)[0] || group.regionName
    const key = normalizeDirectoryName(sidoName)
    const bucket = bySido.get(key)
    if (bucket) bucket.groups.push(group)
    else bySido.set(key, { sidoName, groups: [group] })
  }
  return [...bySido.values()]
}

const positiveInteger = (value: string | undefined) => {
  if (!value || !/^[1-9]\d*$/.test(value)) return null
  const parsed = Number(value)
  return Number.isSafeInteger(parsed) && parsed > 0 ? parsed : null
}

export const parseHospitalDirectoryRoute = (
  segments: string[] | undefined,
): HospitalDirectoryRoute | null => {
  if (!segments || segments.length === 0) return { page: 1 }

  if (segments.length === 2 && segments[0] === "p") {
    const page = positiveInteger(segments[1])
    return page == null ? null : { page }
  }

  const regionId = positiveInteger(segments[0])
  if (regionId != null && segments.length === 1) return { page: 1, regionId }
  if (regionId != null && segments.length === 3 && segments[1] === "p") {
    const page = positiveInteger(segments[2])
    return page == null ? null : { page, regionId }
  }

  const treatmentId = positiveInteger(segments[1])
  if (regionId == null || treatmentId == null) return null

  if (segments.length === 2) return { page: 1, regionId, treatmentId }
  if (segments.length === 4 && segments[2] === "p") {
    const page = positiveInteger(segments[3])
    return page == null ? null : { page, regionId, treatmentId }
  }

  return null
}

export const hospitalDirectoryPath = (
  locale: string,
  { page, regionId, treatmentId }: HospitalDirectoryRoute,
) => {
  const base = regionId != null && treatmentId != null
    ? `/${locale}/hospitals/${regionId}/${treatmentId}`
    : regionId != null
      ? `/${locale}/hospitals/${regionId}`
      : `/${locale}/hospitals`
  return page > 1 ? `${base}/p/${page}` : base
}

export const buildHospitalDirectoryRegions = (
  regions: Region[],
): HospitalDirectoryRegion[] => regions
  .flatMap((parent) => parent.children
    .filter((child) => child.id !== parent.id)
    .map((child) => ({
      regionId: child.id,
      regionName: `${parent.name} ${child.name}`,
    })))
  .sort((left, right) => compareHospitalDirectoryRegionNames(left.regionName, right.regionName))

export const findHospitalDirectoryRegion = (
  regions: HospitalDirectoryRegion[],
  route: HospitalDirectoryRoute,
) => regions.find((region) => region.regionId === route.regionId)

export const buildHospitalDirectoryGroups = (
  rankingGroups: RankingGroupApiItem[],
  regions: Region[],
  treatments: FilterValue[],
): HospitalDirectoryGroup[] => {
  // parent와 같은 ID인 "서울 전체"류는 검색 API에서 0건이므로 허브를 만들지 않는다.
  // ranking API에는 이 집계 그룹이 있어도, 카테고리는 실제 검색 결과가 있는 leaf-region만 연다.
  const regionNames = new Map(
    buildHospitalDirectoryRegions(regions)
      .map((region) => [region.regionId, region.regionName]),
  )
  const treatmentNames = new Map(treatments.map((treatment) => [treatment.id, treatment.name]))

  const unique = new Map<string, HospitalDirectoryGroup>()
  for (const group of rankingGroups) {
    const regionName = regionNames.get(group.regionCategoryId)
    const treatmentName = treatmentNames.get(group.treatmentCategoryId)
    if (!regionName || !treatmentName) continue
    const key = `${group.regionCategoryId}:${group.treatmentCategoryId}`
    unique.set(key, {
      regionId: group.regionCategoryId,
      regionName,
      treatmentId: group.treatmentCategoryId,
      treatmentName,
    })
  }

  return [...unique.values()].sort((left, right) =>
    compareHospitalDirectoryRegionNames(left.regionName, right.regionName) ||
    compareHospitalDirectoryTreatmentNames(left.treatmentName, right.treatmentName),
  )
}

export const findHospitalDirectoryGroup = (
  groups: HospitalDirectoryGroup[],
  route: HospitalDirectoryRoute,
) => groups.find(
  (group) => group.regionId === route.regionId && group.treatmentId === route.treatmentId,
)

export const resolveRankingDirectoryLink = ({
  locale,
  regions,
  regionId,
  treatmentId,
  hasRankingGroup,
}: {
  locale: string
  regions: HospitalDirectoryRegion[]
  regionId: number
  treatmentId: number
  hasRankingGroup: boolean
}) => {
  const region = regions.find((item) => item.regionId === regionId)
  if (!hasRankingGroup || !region) {
    return {
      href: hospitalDirectoryPath(locale, { page: 1 }),
      label: "전체 병원 디렉터리 보기",
    }
  }

  return {
    href: hospitalDirectoryPath(locale, { page: 1, regionId, treatmentId }),
    label: "이 지역·진료과의 전체 병원 보기",
  }
}

export const resolveRelatedHospitalDirectoryLink = ({
  locale,
  regions,
  regionIds,
  treatmentId,
  rankingGroups,
}: {
  locale: string
  regions: HospitalDirectoryRegion[]
  regionIds: number[]
  treatmentId: number
  rankingGroups: RankingGroupApiItem[]
}) => {
  const region = regions.find((item) => regionIds.includes(item.regionId))
  if (!region) return null

  const group = rankingGroups.find((item) =>
    item.regionCategoryId === region.regionId && item.treatmentCategoryId === treatmentId,
  )
  if (group && treatmentId > 0) {
    return {
      href: hospitalDirectoryPath(locale, {
        page: 1,
        regionId: region.regionId,
        treatmentId,
      }),
      label: "같은 지역·진료과 전체 병원 보기",
    }
  }

  return {
    href: hospitalDirectoryPath(locale, { page: 1, regionId: region.regionId }),
    label: "같은 지역 병원 전체 보기",
  }
}

export const HOSPITAL_DIRECTORY_PAGE_WINDOW = 2

// 컴팩트 페이지네이션: 현재±2 window + 1·마지막 페이지 앵커만 노출한다.
// v1의 50-구간 링크(milestone·blockPages)는 사용자 결정으로 제거됐다 —
// "임의 페이지 2클릭 도달" 계약은 폐기, 깊은 목록 발견은 sitemap·허브가 담당한다.
export const buildDirectoryPageLinks = (currentPage: number, totalPages: number) => {
  const windowStart = Math.max(1, currentPage - HOSPITAL_DIRECTORY_PAGE_WINDOW)
  const windowEnd = Math.min(totalPages, currentPage + HOSPITAL_DIRECTORY_PAGE_WINDOW)
  const windowPages = Array.from(
    { length: windowEnd - windowStart + 1 },
    (_, index) => windowStart + index,
  )

  return {
    windowPages,
    leadingPage: windowStart > 1 ? 1 : null,
    leadingEllipsis: windowStart > 2,
    trailingPage: windowEnd < totalPages ? totalPages : null,
    trailingEllipsis: windowEnd < totalPages - 1,
  }
}
